import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import saasPlatformService from '../../services/saasPlatformService';
import './SaasPlatform.css';

const SaasClinicalConfig = () => {
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [investigations, setInvestigations] = useState([]);
  const [selectedInvestigation, setSelectedInvestigation] = useState(null);
  const [ranges, setRanges] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [loadingInv, setLoadingInv] = useState(false);
  const [loadingRanges, setLoadingRanges] = useState(false);
  const [searchInv, setSearchInv] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Draft Creation State
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [selectedRangeForDraft, setSelectedRangeForDraft] = useState(null);
  const [draftForm, setDraftForm] = useState({
    minAgeDays: 0,
    maxAgeDays: 36500,
    gender: 'B',
    minReference: '',
    maxReference: '',
    criticalLow: '',
    criticalHigh: '',
    units: '',
    methodology: 'Automated Cell Counter',
    rationale: 'Updated clinical threshold per diagnostic lab protocol'
  });
  const [draftSubmitting, setDraftSubmitting] = useState(false);

  // Pathologist Approval State
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [rangeToApprove, setRangeToApprove] = useState(null);
  const [approvalForm, setApprovalForm] = useState({
    approverName: 'Dr. S. Sharma, MD Pathologist',
    comments: 'Reviewed clinical precision metrics and approved for live diagnostic reporting.'
  });
  const [approving, setApproving] = useState(false);

  const navigate = useNavigate();

  // Load tenants for selection
  useEffect(() => {
    const fetchTenants = async () => {
      setLoadingTenants(true);
      try {
        const res = await saasPlatformService.getTenants();
        const tenantList = res.data || res || [];
        setTenants(tenantList);
        if (tenantList.length > 0) {
          // Default to tenant 9111 (Alpha) if available
          const defaultTenant = tenantList.find(t => t.tenantId === 9111) || tenantList[0];
          setSelectedTenantId(defaultTenant.tenantId);
        }
      } catch (err) {
        console.error('Failed to load tenants', err);
        setError('Failed to load tenants list.');
      } finally {
        setLoadingTenants(false);
      }
    };
    fetchTenants();
  }, []);

  // Load investigations when tenant changes
  useEffect(() => {
    if (!selectedTenantId) return;
    const fetchInvestigations = async () => {
      setLoadingInv(true);
      setError(null);
      setSelectedInvestigation(null);
      setRanges([]);
      try {
        const res = await saasPlatformService.getTenantInvestigations(selectedTenantId);
        const invList = res.data || res || [];
        setInvestigations(invList);
        if (invList.length > 0) {
          setSelectedInvestigation(invList[0]);
        }
      } catch (err) {
        console.error('Failed to load investigations for tenant', err);
        setError(err?.response?.data?.message || 'Failed to load investigations catalogue.');
      } finally {
        setLoadingInv(false);
      }
    };
    fetchInvestigations();
  }, [selectedTenantId]);

  // Load ranges when investigation changes
  useEffect(() => {
    if (!selectedTenantId || !selectedInvestigation) return;
    const fetchRanges = async () => {
      setLoadingRanges(true);
      try {
        const invId = selectedInvestigation.investigationId || selectedInvestigation.InvestigationID;
        const res = await saasPlatformService.getInvestigationRanges(selectedTenantId, invId);
        setRanges(res.data || res || []);
      } catch (err) {
        console.error('Failed to load ranges', err);
        setError('Failed to load reference ranges.');
      } finally {
        setLoadingRanges(false);
      }
    };
    fetchRanges();
  }, [selectedTenantId, selectedInvestigation]);

  const filteredInvestigations = investigations.filter(inv => {
    const term = searchInv.toLowerCase();
    const code = (inv.investigationCode || inv.InvestigationCode || '').toLowerCase();
    const name = (inv.investigationName || inv.InvestigationName || '').toLowerCase();
    return code.includes(term) || name.includes(term);
  });

  const handleOpenDraftModal = (range) => {
    setSelectedRangeForDraft(range);
    setDraftForm({
      minAgeDays: range.minAgeDays ?? 0,
      maxAgeDays: range.maxAgeDays ?? 36500,
      gender: range.gender || 'B',
      minReference: range.minReference ?? '',
      maxReference: range.maxReference ?? '',
      criticalLow: range.criticalLow ?? '',
      criticalHigh: range.criticalHigh ?? '',
      units: range.units || '',
      methodology: range.methodology || 'Automated Cell Counter',
      rationale: 'Updated clinical threshold per diagnostic lab protocol'
    });
    setShowDraftModal(true);
  };

  const handleSubmitDraft = async (e) => {
    e.preventDefault();
    if (!selectedTenantId || !selectedRangeForDraft) return;
    setDraftSubmitting(true);
    setError(null);
    try {
      const payload = {
        observationId: selectedRangeForDraft.observationId,
        centreId: selectedRangeForDraft.centreId || null,
        minAgeDays: parseInt(draftForm.minAgeDays, 10),
        maxAgeDays: parseInt(draftForm.maxAgeDays, 10),
        gender: draftForm.gender,
        minReference: draftForm.minReference === '' ? null : parseFloat(draftForm.minReference),
        maxReference: draftForm.maxReference === '' ? null : parseFloat(draftForm.maxReference),
        criticalLow: draftForm.criticalLow === '' ? null : parseFloat(draftForm.criticalLow),
        criticalHigh: draftForm.criticalHigh === '' ? null : parseFloat(draftForm.criticalHigh),
        units: draftForm.units,
        methodology: draftForm.methodology,
        effectiveFrom: new Date().toISOString(),
        changeRationale: draftForm.rationale
      };

      await saasPlatformService.createRangeDraft(selectedTenantId, payload);
      setSuccess(`New draft configuration created successfully. Awaiting Pathologist clinical review.`);
      setShowDraftModal(false);
      // Refresh ranges
      const invId = selectedInvestigation.investigationId || selectedInvestigation.InvestigationID;
      const res = await saasPlatformService.getInvestigationRanges(selectedTenantId, invId);
      setRanges(res.data || res || []);
    } catch (err) {
      console.error('Failed to submit draft', err);
      setError(err?.response?.data?.message || 'Failed to submit range draft.');
    } finally {
      setDraftSubmitting(false);
    }
  };

  const handleOpenApproveModal = (range) => {
    setRangeToApprove(range);
    setShowApproveModal(true);
  };

  const handleApproveDraft = async (e) => {
    e.preventDefault();
    if (!selectedTenantId || !rangeToApprove) return;
    setApproving(true);
    setError(null);
    try {
      const versionId = rangeToApprove.versionId || rangeToApprove.VersionID;
      await saasPlatformService.approveRangeDraft(selectedTenantId, versionId, {
        approverName: approvalForm.approverName,
        comments: approvalForm.comments
      });
      setSuccess(`Range version #${versionId} approved and activated for clinical reporting!`);
      setShowApproveModal(false);
      // Refresh ranges
      const invId = selectedInvestigation.investigationId || selectedInvestigation.InvestigationID;
      const res = await saasPlatformService.getInvestigationRanges(selectedTenantId, invId);
      setRanges(res.data || res || []);
    } catch (err) {
      console.error('Failed to approve draft', err);
      setError(err?.response?.data?.message || 'Approval failed.');
    } finally {
      setApproving(false);
    }
  };

  const currentTenantObj = tenants.find(t => t.tenantId === Number(selectedTenantId));

  return (
    <div className="saas-container">
      {/* Header */}
      <div className="saas-header">
        <div className="saas-header-title">
          <h1>
            <span>🔬</span> Investigation Catalogue &amp; Reference Range Governance
          </h1>
          <p>Tenant-Isolated Diagnostic Reference Ranges, Units, Calculation Rules &amp; Pathologist Verification</p>
        </div>
        <div className="saas-header-actions">
          <button className="saas-btn saas-btn-secondary" onClick={() => navigate('/saas/tenants')}>
            ← Back to Tenants
          </button>
        </div>
      </div>

      {/* Tenant Selector Strip */}
      <div className="saas-card" style={{ marginBottom: '1.25rem', padding: '1rem 1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem', color: '#334155' }}>
              Select Client / Tenant:
            </label>
            <select
              className="saas-select"
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(Number(e.target.value))}
              disabled={loadingTenants}
              style={{ minWidth: '320px', fontWeight: 600 }}
            >
              {tenants.map(t => (
                <option key={t.tenantId} value={t.tenantId}>
                  #{t.tenantId} — {t.tenantCode} ({t.organizationName}) [{t.databaseName}]
                </option>
              ))}
            </select>
          </div>

          {currentTenantObj && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <span className="saas-badge saas-badge-info">Isolation: {currentTenantObj.isolationModel || 'SeparateDatabase'}</span>
              <span className="saas-badge saas-badge-active">Database: {currentTenantObj.databaseName}</span>
              <span className="saas-badge saas-badge-primary">Tenant #{currentTenantObj.tenantId}</span>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="saas-error-box">
          <div><strong>Clinical Config Alert:</strong> {error}</div>
          <button className="saas-btn saas-btn-secondary" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {success && (
        <div className="saas-success-box">
          <div><strong>Clinical Governance Notice:</strong> {success}</div>
          <button className="saas-btn saas-btn-secondary" onClick={() => setSuccess(null)}>✕</button>
        </div>
      )}

      {/* Main Two-Column Layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '1.5rem' }}>
        {/* Left Column: Investigation Catalog List */}
        <div className="saas-card" style={{ height: 'fit-content' }}>
          <div className="saas-card-header">
            <h3>Investigation Catalog ({investigations.length})</h3>
          </div>
          <div style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #e2e8f0' }}>
            <input
              type="text"
              className="saas-search-input"
              placeholder="Search tests (CBC, LFT, etc)..."
              value={searchInv}
              onChange={(e) => setSearchInv(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {loadingInv ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>⏳ Loading investigations...</div>
            ) : filteredInvestigations.length > 0 ? (
              filteredInvestigations.map(inv => {
                const id = inv.investigationId || inv.InvestigationID;
                const isSelected = selectedInvestigation && (selectedInvestigation.investigationId || selectedInvestigation.InvestigationID) === id;
                return (
                  <div
                    key={id}
                    onClick={() => setSelectedInvestigation(inv)}
                    style={{
                      padding: '0.75rem 1rem',
                      borderBottom: '1px solid #f1f5f9',
                      cursor: 'pointer',
                      background: isSelected ? '#f0f9ff' : 'transparent',
                      borderLeft: isSelected ? '4px solid #0284c7' : '4px solid transparent',
                      transition: 'background 0.15s ease'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong style={{ fontSize: '0.875rem', color: isSelected ? '#0284c7' : '#1e293b' }}>
                        {inv.investigationName || inv.InvestigationName}
                      </strong>
                      <span style={{ fontSize: '0.75rem', fontFamily: 'monospace', color: '#64748b' }}>
                        {inv.investigationCode || inv.InvestigationCode}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.25rem' }}>
                      Dept: {inv.departmentName || inv.DepartmentName || 'Pathology'} • Sample: {inv.sampleType || inv.SampleType || 'Blood'}
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>No investigations found.</div>
            )}
          </div>
        </div>

        {/* Right Column: Observation Parameters & Reference Ranges Manager */}
        <div>
          {selectedInvestigation ? (
            <div className="saas-card">
              <div className="saas-card-header">
                <div>
                  <h3 style={{ margin: 0 }}>
                    {selectedInvestigation.investigationName || selectedInvestigation.InvestigationName}
                  </h3>
                  <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
                    Code: <strong>{selectedInvestigation.investigationCode || selectedInvestigation.InvestigationCode}</strong> | Isolated Tenant: <strong>{currentTenantObj?.tenantCode}</strong>
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <span className="saas-badge saas-badge-active">
                    ● Isolated in {currentTenantObj?.databaseName}
                  </span>
                </div>
              </div>

              <div className="saas-card-body">
                <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '6px', marginBottom: '1.25rem', border: '1px solid #e2e8f0', fontSize: '0.8125rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem' }}>
                    <div>
                      <span style={{ color: '#64748b' }}>Department:</span><br />
                      <strong>{selectedInvestigation.departmentName || selectedInvestigation.DepartmentName || 'Pathology'}</strong>
                    </div>
                    <div>
                      <span style={{ color: '#64748b' }}>Sample / Container:</span><br />
                      <strong>{selectedInvestigation.sampleType || selectedInvestigation.SampleType || 'EDTA Whole Blood'}</strong>
                    </div>
                    <div>
                      <span style={{ color: '#64748b' }}>Methodology:</span><br />
                      <strong>Automated Cell Counter / Flow Cytometry</strong>
                    </div>
                    <div>
                      <span style={{ color: '#64748b' }}>Clinical Governance:</span><br />
                      <strong style={{ color: '#0284c7' }}>Pathologist Sign-off Required</strong>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <h4 style={{ margin: 0, fontSize: '0.9375rem', color: '#0f172a' }}>
                    Reference Ranges &amp; Version History ({ranges.length})
                  </h4>
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    Changes strictly scoped to tenant database without cross-tenant impact.
                  </span>
                </div>

                {loadingRanges ? (
                  <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
                    ⏳ Loading reference range versions from {currentTenantObj?.databaseName}...
                  </div>
                ) : ranges.length > 0 ? (
                  <div className="saas-table-container">
                    <table className="saas-table">
                      <thead>
                        <tr>
                          <th>Parameter</th>
                          <th>Gender / Age</th>
                          <th>Reference Range</th>
                          <th>Critical Limits</th>
                          <th>Units</th>
                          <th>Status / Version</th>
                          <th>Effective Date</th>
                          <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ranges.map((r, idx) => {
                          const isDraft = r.approvalStatus === 'DRAFT' || !r.isActive;
                          return (
                            <tr key={r.versionId || idx} style={{ background: isDraft ? '#fffbeb' : 'transparent' }}>
                              <td>
                                <strong>{r.observationName || `Obs #${r.observationId}`}</strong>
                                {r.changeRationale && (
                                  <div style={{ fontSize: '0.75rem', color: '#64748b', fontStyle: 'italic' }}>
                                    "{r.changeRationale}"
                                  </div>
                                )}
                              </td>
                              <td>
                                <div>{r.gender === 'M' ? '♂ Male' : r.gender === 'F' ? '♀ Female' : 'Both'}</div>
                                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                  {Math.round(r.minAgeDays / 365)} - {Math.round(r.maxAgeDays / 365)} yrs
                                </div>
                              </td>
                              <td>
                                <strong style={{ color: '#0284c7' }}>
                                  {r.minReference != null ? r.minReference : '—'} - {r.maxReference != null ? r.maxReference : '—'}
                                </strong>
                              </td>
                              <td>
                                {r.criticalLow != null || r.criticalHigh != null ? (
                                  <span style={{ color: '#b91c1c', fontWeight: 600, fontSize: '0.8125rem' }}>
                                    &lt; {r.criticalLow ?? '—'} / &gt; {r.criticalHigh ?? '—'}
                                  </span>
                                ) : (
                                  <span style={{ color: '#94a3b8' }}>None</span>
                                )}
                              </td>
                              <td>
                                <span style={{ fontFamily: 'monospace' }}>{r.units || '—'}</span>
                              </td>
                              <td>
                                {r.isActive ? (
                                  <span className="saas-badge saas-badge-active">
                                    v{r.versionNumber || 1} ACTIVE
                                  </span>
                                ) : (
                                  <span className="saas-badge saas-badge-warning">
                                    v{r.versionNumber || 1} DRAFT
                                  </span>
                                )}
                                {r.approvedBy && (
                                  <div style={{ fontSize: '0.7rem', color: '#047857', marginTop: '0.2rem' }}>
                                    Approved by {r.approvedBy}
                                  </div>
                                )}
                              </td>
                              <td style={{ fontSize: '0.75rem', color: '#64748b' }}>
                                {r.effectiveFrom ? new Date(r.effectiveFrom).toLocaleDateString() : 'Immediate'}
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                <div style={{ display: 'flex', gap: '0.35rem', justifyContent: 'flex-end' }}>
                                  <button
                                    className="saas-btn saas-btn-secondary"
                                    style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                                    onClick={() => handleOpenDraftModal(r)}
                                    title="Create a revised reference range draft"
                                  >
                                    ✏️ New Draft
                                  </button>
                                  {isDraft && (
                                    <button
                                      className="saas-btn saas-btn-success"
                                      style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                                      onClick={() => handleOpenApproveModal(r)}
                                      title="Review and approve as Pathologist"
                                    >
                                      ✅ Sign-off
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8' }}>
                    No reference ranges found for this investigation. Click below to initialize baseline ranges.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="saas-card" style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
              Select an investigation from the left column to view and govern its clinical reference ranges.
            </div>
          )}
        </div>
      </div>

      {/* Modal: Create Range Draft */}
      {showDraftModal && selectedRangeForDraft && (
        <div className="saas-modal-backdrop" onClick={() => setShowDraftModal(false)}>
          <div className="saas-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '650px' }}>
            <div className="saas-modal-header">
              <h3>
                Create Revised Range Draft: {selectedRangeForDraft.observationName}
              </h3>
              <button style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer' }} onClick={() => setShowDraftModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmitDraft}>
              <div className="saas-modal-body">
                <div style={{ background: '#f8fafc', padding: '0.75rem', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.8125rem' }}>
                  Target Tenant: <strong>{currentTenantObj?.tenantCode} ({currentTenantObj?.organizationName})</strong><br />
                  Current Active Range: <strong>{selectedRangeForDraft.minReference} - {selectedRangeForDraft.maxReference} {selectedRangeForDraft.units}</strong>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label className="saas-form-label">Min Reference Value</label>
                    <input
                      type="number"
                      step="any"
                      className="saas-form-input"
                      value={draftForm.minReference}
                      onChange={(e) => setDraftForm({ ...draftForm, minReference: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="saas-form-label">Max Reference Value</label>
                    <input
                      type="number"
                      step="any"
                      className="saas-form-input"
                      value={draftForm.maxReference}
                      onChange={(e) => setDraftForm({ ...draftForm, maxReference: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label className="saas-form-label">Critical Low (Panic Limit)</label>
                    <input
                      type="number"
                      step="any"
                      className="saas-form-input"
                      value={draftForm.criticalLow}
                      onChange={(e) => setDraftForm({ ...draftForm, criticalLow: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="saas-form-label">Critical High (Panic Limit)</label>
                    <input
                      type="number"
                      step="any"
                      className="saas-form-input"
                      value={draftForm.criticalHigh}
                      onChange={(e) => setDraftForm({ ...draftForm, criticalHigh: e.target.value })}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label className="saas-form-label">Reporting Units</label>
                    <input
                      type="text"
                      className="saas-form-input"
                      value={draftForm.units}
                      onChange={(e) => setDraftForm({ ...draftForm, units: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="saas-form-label">Methodology</label>
                    <input
                      type="text"
                      className="saas-form-input"
                      value={draftForm.methodology}
                      onChange={(e) => setDraftForm({ ...draftForm, methodology: e.target.value })}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label className="saas-form-label">Clinical Justification / Rationale</label>
                  <textarea
                    className="saas-form-input"
                    rows={2}
                    value={draftForm.rationale}
                    onChange={(e) => setDraftForm({ ...draftForm, rationale: e.target.value })}
                    required
                  />
                </div>

                {/* Side-by-Side Diff Preview */}
                <div className="saas-diff-container">
                  <div className="saas-diff-pane old">
                    <div className="saas-diff-title">Previous Baseline (Active)</div>
                    <div>Range: <strong>{selectedRangeForDraft.minReference} - {selectedRangeForDraft.maxReference}</strong></div>
                    <div>Critical: &lt; {selectedRangeForDraft.criticalLow ?? '—'} / &gt; {selectedRangeForDraft.criticalHigh ?? '—'}</div>
                    <div>Units: {selectedRangeForDraft.units || '—'}</div>
                  </div>
                  <div className="saas-diff-pane new">
                    <div className="saas-diff-title">Proposed Revision (Draft)</div>
                    <div>Range: <strong>{draftForm.minReference || '—'} - {draftForm.maxReference || '—'}</strong></div>
                    <div>Critical: &lt; {draftForm.criticalLow || '—'} / &gt; {draftForm.criticalHigh || '—'}</div>
                    <div>Units: {draftForm.units || '—'}</div>
                  </div>
                </div>
              </div>

              <div className="saas-modal-footer">
                <button type="button" className="saas-btn saas-btn-secondary" onClick={() => setShowDraftModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="saas-btn saas-btn-primary" disabled={draftSubmitting}>
                  {draftSubmitting ? 'Saving Draft...' : '💾 Save Draft for Pathologist Review'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Pathologist Sign-Off / Approval */}
      {showApproveModal && rangeToApprove && (
        <div className="saas-modal-backdrop" onClick={() => setShowApproveModal(false)}>
          <div className="saas-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="saas-modal-header">
              <h3>
                Pathologist Clinical Sign-off &amp; Activation
              </h3>
              <button style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer' }} onClick={() => setShowApproveModal(false)}>✕</button>
            </div>
            <form onSubmit={handleApproveDraft}>
              <div className="saas-modal-body">
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '1rem', borderRadius: '6px', marginBottom: '1.25rem', color: '#166534' }}>
                  <strong>Diagnostic Accuracy Assurance:</strong><br />
                  You are activating revised reference values for <strong>{rangeToApprove.observationName}</strong> in <strong>{currentTenantObj?.tenantCode}</strong>.
                  Historical finalized patient reports will remain pinned to their original configurations.
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label className="saas-form-label">Designated Pathologist / Clinical Director</label>
                  <input
                    type="text"
                    className="saas-form-input"
                    value={approvalForm.approverName}
                    onChange={(e) => setApprovalForm({ ...approvalForm, approverName: e.target.value })}
                    required
                  />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label className="saas-form-label">Pathology Approval Notes &amp; Verification Rationale</label>
                  <textarea
                    className="saas-form-input"
                    rows={3}
                    value={approvalForm.comments}
                    onChange={(e) => setApprovalForm({ ...approvalForm, comments: e.target.value })}
                    required
                  />
                </div>

                <div style={{ fontSize: '0.8125rem', color: '#64748b' }}>
                  Effective Date: <strong>{new Date().toLocaleString()}</strong> (Immediate Activation)
                </div>
              </div>

              <div className="saas-modal-footer">
                <button type="button" className="saas-btn saas-btn-secondary" onClick={() => setShowApproveModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="saas-btn saas-btn-success" disabled={approving}>
                  {approving ? 'Activating...' : '✅ Authorize & Activate Configuration'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaasClinicalConfig;
